import WebviewComponentAdaptor from "@shared/components/application/other/WebviewComponentAdaptor/WebviewComponentAdaptor.web";
import { postMessageToReactNativeWebView } from "@shared/utils/webview";
import Button from "@ui-kit/form/Button/Button.web";
import { useTranslation } from "@shared/utils/internationalization";
import Link from "next/link";

type AddToDigitalWalletButtonProps = {
  isLoggedIn: boolean;
  myOrderUrl: string;
  stagingPageUrl: string;
  transactionId: string;
};

/**
 * A button component that allows users to add their order to their digital wallet.
 * The component adapts its behavior based on the platform:
 * - In web environments: Renders a link to the appropriate order page
 * - In React Native webview: Sends a message to the native app to handle the action
 *
 */
export default function AddToDigitalWalletButton(
  props: AddToDigitalWalletButtonProps
): JSX.Element {
  return (
    <WebviewComponentAdaptor
      webComponent={<AddToDigitalWalletButton.WebComponent {...props} />}
      webviewComponent={
        <AddToDigitalWalletButton.WebviewComponent {...props} />
      }
    />
  );
}

AddToDigitalWalletButton.WebviewComponent = function WebviewComponent({
  isLoggedIn,
  transactionId,
  stagingPageUrl,
}: Omit<AddToDigitalWalletButtonProps, "myOrderUrl">) {
  const postOrderMessage = () =>
    postMessageToReactNativeWebView({
      action: "VIEW_ORDER",
      payload: isLoggedIn
        ? { transactionId }
        : { guestCheckoutOrderUrl: stagingPageUrl },
    });

  return (
    <AddToDigitalWalletButton.ButtonComponent onClick={postOrderMessage} />
  );
};

AddToDigitalWalletButton.WebComponent = function WebComponent({
  isLoggedIn,
  myOrderUrl,
  stagingPageUrl,
}: Omit<AddToDigitalWalletButtonProps, "transactionId">) {
  return (
    <Link
      href={isLoggedIn ? myOrderUrl : stagingPageUrl}
      target="_blank"
      passHref
      style={{ width: "100%" }}
    >
      <AddToDigitalWalletButton.ButtonComponent />
    </Link>
  );
};

AddToDigitalWalletButton.ButtonComponent = function ButtonComponent({
  onClick,
}: {
  onClick?: () => void;
}) {
  const { t } = useTranslation("checkout");

  return (
    <Button.Solid
      as="span"
      fontWeight={500}
      width="100%"
      data-testid="order-status-add-to-wallet"
      onClick={onClick}
    >
      {t("Add to digital wallet")}
    </Button.Solid>
  );
};
